import { type CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs';
import { AuthService, PermissionService } from '#services';

/**
 * Permission-based route guard.
 * Waits for /auth/me to resolve before checking permissions, preventing
 * a race condition where userDetails is null immediately after login.
 * Redirects to /forbidden when the user lacks the required resource + action combo.
 *
 * Usage in routes:
 *   canActivate: [roleGuard('Admin', 'CanAccess')]
 *   canActivate: [roleGuard('Assets', 'CanCreate')]
 */
export const roleGuard = (resource: string, action: string): CanActivateFn => {
    return () => {
        const permissionService = inject(PermissionService);
        const authService = inject(AuthService);
        const router = inject(Router);

        // Wait until userDetails is populated (i.e. /auth/me has resolved)
        // before evaluating permissions — prevents false /forbidden on page load.
        return toObservable(authService.userDetails).pipe(
            filter(details => details !== null),
            take(1),
            map(() =>
                permissionService.hasPermission(resource, action)
                    ? true
                    : router.createUrlTree(['/forbidden'])
            ),
        );
    };
};
